import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { settingsApi } from "@/api/settingsApi";
import { useToastHandler } from "@/hooks/useToastHandler";
import { ApiError } from "@/types/apiTypes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageTitleScript from "@/components/MetaScript";
import CustomToggleSelect from "@/components/ui/CustomToggleSelect";
import { daysRangeOptions } from "@/lib/options/daysRangeOptions";
import { useEffectiveUser } from "@/hooks/useEffectiveUser";
import { WeightUnit } from "@/types/setTypes";

export function SettingsPreferencesPage() {
  const [defaultUnit, setDefaultUnit] = useState<WeightUnit>("lb");
  const [defaultDateRange, setDefaultDateRange] = useState<number>(30);
  const [isSaving, setIsSaving] = useState(false);
  const { handleSuccess, handleError } = useToastHandler();

  // ######### react hook
  const { user, isLoading, refetchUserInfo } = useEffectiveUser();
  useEffect(() => {
    if (!user) return;
    setDefaultUnit(user.preferred_weight_unit as WeightUnit);
    setDefaultDateRange(user.preferred_days_range);
  }, [user]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Use the settingsApi to update preferences
      await settingsApi.updatePreferences({
        preferred_weight_unit: defaultUnit,
        preferred_days_range: defaultDateRange,
      });

      handleSuccess("Preferences saved successfully!", 1000);
      refetchUserInfo();
    } catch (err: unknown) {
      console.error(err);
      handleError(
        err instanceof ApiError ? err.message : "Failed to save preferences"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col p-6">
      <PageTitleScript title="Preferences" />
      <div className="flex items-center mb-6">
        <Link to="/settings" className="mr-2">
          <ArrowLeft size={24} />
        </Link>
        <h1 className="text-2xl font-bold text-tx-dark">Preferences</h1>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bd-dark"></div>
        </div>
      ) : (
        <>
          <div className="rounded-lg shadow-md bg-white p-8 mb-4">
            {/* Default Unit */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Default Unit
              </label>
              <CustomToggleSelect
                value={defaultUnit}
                className="w-1/2"
                setValue={(val) => setDefaultUnit(val as WeightUnit)}
                options={[
                  { label: "lb", value: "lb" },
                  { label: "kg", value: "kg" },
                ]}
              />
            </div>

            {/* Default Date Range */}
            <div>
              <label
                htmlFor="date-range-select"
                className="block text-sm font-medium mb-2"
              >
                Default Date Range for Charts
              </label>
              <Select
                value={String(defaultDateRange)}
                onValueChange={(value) => setDefaultDateRange(Number(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  {daysRangeOptions.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            variant="primary"
            size="lg"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Preferences"}
          </Button>
        </>
      )}
    </div>
  );
}
